import React, { useState, useEffect } from 'react';
import { triggerNotificationPrompt } from '../components/NotificationOnboarding';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Plus, Search, Filter, MoreVertical, Calendar, User, Edit2, Trash2, CheckCircle2, Clock, Play, X, Briefcase, Paperclip, Download, FileText, Send, MessageSquare, Loader2, LayoutList, Columns3 } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { tasksAPI, teamAPI } from '../services/api';
import { toast } from 'sonner';
import DatePicker from '../components/DatePicker';

const Tasks = () => {
  const [tasks, setTasks] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    assigned_to: '',
    priority: 'medium',
    due_date: '',
    category: ''
  });
  const [attachFiles, setAttachFiles] = useState([]);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [commentLoading, setCommentLoading] = useState(false);
  const [viewMode, setViewMode] = useState('list');
  const [draggedTask, setDraggedTask] = useState(null);
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

  // Ctrl+N shortcut to open create task dialog
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'n') {
        e.preventDefault();
        setIsCreateDialogOpen(true);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  useEffect(() => {
    fetchData();
  }, [filterStatus]);

  const fetchData = async () => {
    try {
      const params = filterStatus !== 'all' ? { status: filterStatus } : {};
      const [tasksRes, teamRes] = await Promise.all([
        tasksAPI.getAll(params),
        teamAPI.getAll()
      ]);
      setTasks(tasksRes.data);
      setTeamMembers(teamRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  const filteredTasks = tasks.filter(task => {
    const matchesSearch = task.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         task.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  const handleCreateTask = async () => {
    try {
      const response = await tasksAPI.create(newTask);
      const createdTask = response.data;
      
      // Upload attachments if any
      if (attachFiles.length > 0 && createdTask.id) {
        try {
          await tasksAPI.uploadAttachments(createdTask.id, attachFiles);
        } catch (err) {
          console.error('Attachment upload failed:', err);
          toast.error('Task created but file upload failed');
        }
      }
      
      setTasks([createdTask, ...tasks]);
      setIsCreateDialogOpen(false);
      setNewTask({
        title: '',
        description: '',
        assigned_to: '',
        priority: 'medium',
        due_date: '',
        category: ''
      });
      setAttachFiles([]);
      toast.success('Task created successfully!');
      triggerNotificationPrompt();
    } catch (error) {
      console.error('Failed to create task:', error);
      toast.error(error.response?.data?.detail || 'Failed to create task');
    }
  };

  const handleUpdateStatus = async (task, newStatus) => {
    try {
      const taskId = task.id || task._id;
      await tasksAPI.update(taskId, { status: newStatus });
      setTasks(tasks.map(t => 
        (t.id || t._id) === taskId ? { ...t, status: newStatus } : t
      ));
      toast.success(`Task marked as ${newStatus.replace('_', ' ')}`);
    } catch (error) {
      console.error('Failed to update task:', error);
      toast.error('Failed to update task status');
    }
  };

  const handleEditTask = async () => {
    if (!selectedTask) return;
    try {
      const taskId = selectedTask.id || selectedTask._id;
      await tasksAPI.update(taskId, selectedTask);
      setTasks(tasks.map(t => 
        (t.id || t._id) === taskId ? selectedTask : t
      ));
      setIsEditDialogOpen(false);
      toast.success('Task updated successfully!');
    } catch (error) {
      console.error('Failed to update task:', error);
      toast.error('Failed to update task');
    }
  };

  const handleDeleteTask = async (task) => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;
    try {
      const taskId = task.id || task._id;
      await tasksAPI.delete(taskId);
      setTasks(tasks.filter(t => (t.id || t._id) !== taskId));
      toast.success('Task deleted successfully!');
    } catch (error) {
      console.error('Failed to delete task:', error);
      toast.error('Failed to delete task');
    }
  };

  const openViewDialog = (task) => {
    setSelectedTask(task);
    setIsViewDialogOpen(true);
    setComments([]);
    const taskId = task.id || task._id;
    if (taskId) {
      tasksAPI.getComments(taskId).then(res => setComments(res.data || [])).catch(() => {});
    }
  };

  const openEditDialog = (task) => {
    setSelectedTask({ ...task });
    setIsEditDialogOpen(true);
    setComments([]);
    setNewComment('');
    // Load comments
    const taskId = task.id || task._id;
    if (taskId) {
      tasksAPI.getComments(taskId).then(res => setComments(res.data || [])).catch(() => {});
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !selectedTask) return;
    const taskId = selectedTask.id || selectedTask._id;
    setCommentLoading(true);
    try {
      const res = await tasksAPI.addComment(taskId, newComment.trim());
      setComments(prev => [...prev, res.data]);
      setNewComment('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add comment');
    } finally {
      setCommentLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-700 border-green-200';
      case 'in_progress': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'pending': return 'bg-orange-100 text-orange-700 border-orange-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-700 border-red-200';
      case 'medium': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'low': return 'bg-gray-100 text-gray-700 border-gray-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  return (
    <>
      <div className="space-y-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-medium tracking-tight text-[#0A0D0A]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Tasks</h1>
            <p className="text-[#4B5563] mt-1 text-sm">Manage and track your team's tasks</p>
          </div>
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#5FD068] hover:bg-[#4ec057] text-white w-full sm:w-auto" data-testid="create-task-btn">
                <Plus className="w-4 h-4 mr-2" />
                Create Task
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Create New Task</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Task Title</Label>
                  <Input
                    id="title"
                    placeholder="Enter task title"
                    value={newTask.title}
                    onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                    data-testid="task-title-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    placeholder="Enter task description"
                    value={newTask.description}
                    onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                    rows={4}
                    data-testid="task-description-input"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="assignedTo">Assign To</Label>
                    <Select value={newTask.assigned_to} onValueChange={(value) => setNewTask({ ...newTask, assigned_to: value })}>
                      <SelectTrigger data-testid="task-assignee-select">
                        <SelectValue placeholder="Select team member" />
                      </SelectTrigger>
                      <SelectContent>
                        {teamMembers.map((member) => (
                          <SelectItem key={member.id || member._id} value={member.id || member._id}>
                            {member.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="priority">Priority</Label>
                    <Select value={newTask.priority} onValueChange={(value) => setNewTask({ ...newTask, priority: value })}>
                      <SelectTrigger data-testid="task-priority-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="low">Low</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="category">Category</Label>
                    <Select value={newTask.category} onValueChange={(value) => setNewTask({ ...newTask, category: value })}>
                      <SelectTrigger data-testid="task-category-select">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="General">General</SelectItem>
                        <SelectItem value="Admin">Admin</SelectItem>
                        <SelectItem value="Data Entry">Data Entry</SelectItem>
                        <SelectItem value="Marketing">Marketing</SelectItem>
                        <SelectItem value="Research">Research</SelectItem>
                        <SelectItem value="Customer Support">Customer Support</SelectItem>
                        <SelectItem value="Content Writing">Content Writing</SelectItem>
                        <SelectItem value="Social Media">Social Media</SelectItem>
                        <SelectItem value="Bookkeeping">Bookkeeping</SelectItem>
                        <SelectItem value="Project Management">Project Management</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="dueDate">Due Date</Label>
                    <DatePicker
                      value={newTask.due_date}
                      onChange={(date) => setNewTask({ ...newTask, due_date: date })}
                      placeholder="Select due date"
                      data-testid="task-due-date-picker"
                    />
                  </div>
                </div>

                {/* File Attachments */}
                <div className="space-y-2">
                  <Label>Attachments</Label>
                  <div className="flex items-center gap-2">
                    <label className="flex items-center gap-2 px-3 py-2 text-sm border border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-[#5FD068] transition-colors" data-testid="attach-files-btn">
                      <Paperclip className="w-4 h-4 text-gray-500" />
                      <span className="text-gray-600">Attach files</span>
                      <input
                        type="file"
                        multiple
                        className="hidden"
                        onChange={(e) => {
                          const files = Array.from(e.target.files || []);
                          setAttachFiles(prev => [...prev, ...files].slice(0, 5));
                          e.target.value = '';
                        }}
                        data-testid="file-input"
                      />
                    </label>
                    <span className="text-xs text-gray-400">Max 5 files, 5MB each</span>
                  </div>
                  {attachFiles.length > 0 && (
                    <div className="space-y-1">
                      {attachFiles.map((f, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm bg-gray-50 px-2 py-1 rounded">
                          <FileText className="w-3 h-3 text-gray-400 shrink-0" />
                          <span className="flex-1 truncate text-gray-700">{f.name}</span>
                          <span className="text-xs text-gray-400 shrink-0">{(f.size / 1024).toFixed(0)}KB</span>
                          <button onClick={() => setAttachFiles(prev => prev.filter((_, idx) => idx !== i))} className="text-red-400 hover:text-red-600">
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <Button
                  onClick={handleCreateTask}
                  className="w-full bg-[#5FD068] hover:bg-[#4ec057] text-white"
                  disabled={!newTask.title || !newTask.assigned_to}
                >
                    Create Task
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  placeholder="Search tasks..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-full md:w-48">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
              <div className="flex w-full md:w-auto border rounded-lg overflow-hidden" data-testid="view-mode-toggle">
                <button
                  onClick={() => setViewMode('list')}
                  className={`flex-1 md:flex-none flex items-center justify-center gap-1.5 px-4 py-2.5 text-sm font-medium transition-colors ${viewMode === 'list' ? 'bg-[#5FD068] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                  data-testid="view-mode-list"
                >
                  <LayoutList className="w-4 h-4" /> List
                </button>
                <button
                  onClick={() => setViewMode('kanban')}
                  className={`flex-1 md:flex-none flex items-center justify-center gap-1.5 px-4 py-2.5 text-sm font-medium transition-colors ${viewMode === 'kanban' ? 'bg-[#5FD068] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                  data-testid="view-mode-kanban"
                >
                  <Columns3 className="w-4 h-4" /> Board
                </button>
              </div>
            </div>
          </CardContent>
        </Card>

        {loading ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Loader2 className="w-8 h-8 text-[#5FD068] animate-spin mx-auto" />
            </CardContent>
          </Card>
        ) : viewMode === 'kanban' ? (
          /* Kanban Board View */
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="kanban-board">
            {[
              { status: 'pending', label: 'Pending', color: 'border-amber-300', bg: 'bg-amber-50', icon: <Clock className="w-4 h-4 text-amber-600" /> },
              { status: 'in_progress', label: 'In Progress', color: 'border-blue-300', bg: 'bg-blue-50', icon: <Play className="w-4 h-4 text-blue-600" /> },
              { status: 'completed', label: 'Completed', color: 'border-green-300', bg: 'bg-green-50', icon: <CheckCircle2 className="w-4 h-4 text-green-600" /> },
            ].map(col => {
              const columnTasks = filteredTasks.filter(t => t.status === col.status);
              return (
                <div
                  key={col.status}
                  className={`rounded-xl border-2 ${col.color} ${col.bg} p-3 min-h-[300px]`}
                  data-testid={`kanban-column-${col.status}`}
                  onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('ring-2', 'ring-[#5FD068]'); }}
                  onDragLeave={(e) => { e.currentTarget.classList.remove('ring-2', 'ring-[#5FD068]'); }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.remove('ring-2', 'ring-[#5FD068]');
                    if (draggedTask && draggedTask.status !== col.status) {
                      handleUpdateStatus(draggedTask, col.status);
                    }
                    setDraggedTask(null);
                  }}
                >
                  <div className="flex items-center gap-2 mb-3 px-1">
                    {col.icon}
                    <h3 className="font-semibold text-sm text-gray-800">{col.label}</h3>
                    <Badge variant="secondary" className="ml-auto text-xs">{columnTasks.length}</Badge>
                  </div>
                  <div className="space-y-2">
                    {columnTasks.map(task => (
                      <div
                        key={task.id || task._id}
                        draggable
                        onDragStart={() => setDraggedTask(task)}
                        onDragEnd={() => setDraggedTask(null)}
                        onClick={() => openViewDialog(task)}
                        className="bg-white rounded-lg border border-gray-200 p-3 cursor-grab active:cursor-grabbing hover:shadow-md transition-all"
                        data-testid={`kanban-task-${task.id || task._id}`}
                      >
                        <h4 className="text-sm font-medium text-gray-900 line-clamp-2">{task.title}</h4>
                        {task.description && (
                          <p className="text-xs text-gray-500 mt-1 line-clamp-1">{task.description}</p>
                        )}
                        <div className="flex items-center justify-between mt-2 gap-2">
                          <Badge variant="outline" className={`text-[10px] ${getPriorityColor(task.priority)}`}>
                            {task.priority}
                          </Badge>
                          {task.assigned_to_name && (
                            <span className="text-[10px] text-gray-500 truncate max-w-[100px]">{task.assigned_to_name}</span>
                          )}
                          {task.due_date && (
                            <span className="text-[10px] text-gray-400 flex items-center gap-0.5">
                              <Calendar className="w-3 h-3" />
                              {new Date(task.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                    {columnTasks.length === 0 && (
                      <p className="text-xs text-gray-400 text-center py-6">No tasks</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <>
            {/* Tasks List */}
            <div className="grid gap-4">
              {filteredTasks.map((task) => (
                <Card 
                  key={task.id || task._id} 
                  className="hover:shadow-lg transition-shadow cursor-pointer overflow-hidden w-full"
                  onClick={() => openViewDialog(task)}
                  data-testid={`task-card-${task.id || task._id}`}
                >
                  <CardContent className="p-4 sm:p-6 max-w-full overflow-hidden">
                    <div className="flex items-start justify-between min-w-0 gap-2">
                      <div className="flex-1 min-w-0 overflow-hidden">
                        <div className="flex items-start justify-between gap-2 mb-3">
                          <div className="min-w-0 flex-1 overflow-hidden">
                            <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2 break-words overflow-wrap-anywhere" style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{task.title}</h3>
                            <p className="text-gray-600 text-sm line-clamp-2 break-words" style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{task.description}</p>
                          </div>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                              <Button variant="ghost" size="icon" data-testid={`task-menu-${task.id || task._id}`}>
                                <MoreVertical className="w-5 h-5" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                              <DropdownMenuItem onClick={() => openViewDialog(task)}>
                                <User className="w-4 h-4 mr-2" />
                                View Details
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => openEditDialog(task)}>
                                <Edit2 className="w-4 h-4 mr-2" />
                                Edit Task
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              {task.status !== 'pending' && (
                                <DropdownMenuItem onClick={() => handleUpdateStatus(task, 'pending')}>
                                  <Clock className="w-4 h-4 mr-2" />
                                  Mark as Pending
                                </DropdownMenuItem>
                              )}
                              {task.status !== 'in_progress' && (
                                <DropdownMenuItem onClick={() => handleUpdateStatus(task, 'in_progress')}>
                                  <Play className="w-4 h-4 mr-2" />
                                  Mark as In Progress
                                </DropdownMenuItem>
                              )}
                              {task.status !== 'completed' && (
                                <DropdownMenuItem onClick={() => handleUpdateStatus(task, 'completed')}>
                                  <CheckCircle2 className="w-4 h-4 mr-2" />
                                  Mark as Completed
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuSeparator />
                              <DropdownMenuItem 
                                className="text-red-600"
                                onClick={() => handleDeleteTask(task)}
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete Task
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                        
                        <div className="flex flex-wrap gap-2 mt-4">
                          <Badge variant="outline" className={`${getStatusColor(task.status)} whitespace-nowrap`}>
                            {task.status.replace('_', ' ')}
                          </Badge>
                          <Badge variant="outline" className={`${getPriorityColor(task.priority)} whitespace-nowrap`}>
                            {task.priority}
                          </Badge>
                          {task.category && (
                            <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200 whitespace-nowrap">
                              {task.category}
                            </Badge>
                          )}
                        </div>

                        <div className="flex flex-col gap-2 mt-4 text-sm text-gray-600">
                          <div className="flex items-center gap-2 min-w-0 overflow-hidden">
                            <User className="w-4 h-4 shrink-0" />
                            <span className="truncate" style={{ overflowWrap: 'anywhere' }}>Assigned to: {task.assigned_to_name || task.assigned_to || 'Unassigned'}</span>
                          </div>
                          {task.assigned_by_name && (
                            <div className="flex items-center gap-2 min-w-0 overflow-hidden">
                              <Briefcase className="w-4 h-4 shrink-0" />
                              <span className="truncate">By: {task.assigned_by_name}</span>
                            </div>
                          )}
                          {task.due_date && (
                            <div className="flex items-center gap-2 whitespace-nowrap">
                              <Calendar className="w-4 h-4 shrink-0" />
                              <span>Due: {task.due_date}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {filteredTasks.length === 0 && (
              <Card>
                <CardContent className="p-12 text-center">
                  <p className="text-gray-500">No tasks found. Create your first task to get started!</p>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>

      {/* View Task Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-[95vw] sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Task Details</DialogTitle>
          </DialogHeader>
          {selectedTask && (
            <div className="space-y-4 py-4 min-w-0 overflow-hidden">
              <div className="min-w-0 overflow-hidden">
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 break-words" style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{selectedTask.title}</h3>
                <div className="flex flex-wrap gap-2 mt-2">
                  <Badge variant="outline" className={getStatusColor(selectedTask.status)}>
                    {selectedTask.status.replace('_', ' ')}
                  </Badge>
                  <Badge variant="outline" className={getPriorityColor(selectedTask.priority)}>
                    {selectedTask.priority} priority
                  </Badge>
                </div>
              </div>
              
              <div className="min-w-0 overflow-hidden">
                <Label className="text-gray-500">Description</Label>
                <p className="text-gray-900 mt-1 break-words whitespace-pre-wrap" style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{selectedTask.description || 'No description'}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="min-w-0">
                  <Label className="text-gray-500">Assigned To</Label>
                  <p className="text-gray-900 mt-1 flex items-center gap-2 min-w-0">
                    <User className="w-4 h-4 shrink-0" />
                    <span className="truncate">{selectedTask.assigned_to_name || selectedTask.assigned_to || 'Unassigned'}</span>
                  </p>
                </div>
                <div className="min-w-0">
                  <Label className="text-gray-500">Assigned By</Label>
                  <p className="text-gray-900 mt-1 flex items-center gap-2 min-w-0">
                    <Briefcase className="w-4 h-4 shrink-0" />
                    <span className="truncate">{selectedTask.assigned_by_name || 'Unknown'}</span>
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Due Date</Label>
                  <p className="text-gray-900 mt-1 flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    {selectedTask.due_date || 'Not set'}
                  </p>
                </div>
              </div>

              {/* Attachments */}
              {selectedTask.attachments && selectedTask.attachments.length > 0 && (
                <div>
                  <Label className="text-gray-500">Attachments ({selectedTask.attachments.length})</Label>
                  <div className="space-y-1 mt-1">
                    {selectedTask.attachments.map((att, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm bg-gray-50 px-3 py-2 rounded-lg">
                        <Paperclip className="w-4 h-4 text-gray-400 shrink-0" />
                        <span className="flex-1 truncate text-gray-700">{att.name}</span>
                        <span className="text-xs text-gray-400 shrink-0">{att.size ? `${(att.size / 1024).toFixed(0)}KB` : ''}</span>
                        <button
                          onClick={async (e) => {
                            e.stopPropagation();
                            try {
                              const res = await tasksAPI.downloadAttachment(selectedTask.id || selectedTask._id, i);
                              const url = URL.createObjectURL(res.data);
                              const a = document.createElement('a');
                              a.href = url;
                              a.download = att.name;
                              a.click();
                              URL.revokeObjectURL(url);
                            } catch { toast.error('Download failed'); }
                          }}
                          className="text-[#5FD068] hover:text-[#4ec057]"
                          data-testid={`download-attachment-${i}`}
                        >
                          <Download className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedTask.category && (
                <div>
                  <Label className="text-gray-500">Category</Label>
                  <p className="text-gray-900 mt-1">{selectedTask.category}</p>
                </div>
              )}

              {/* Comments in View */}
              {comments.length > 0 && (
                <div className="border-t pt-3 mt-3">
                  <Label className="flex items-center gap-2 mb-2 text-gray-500">
                    <MessageSquare className="w-4 h-4" />
                    Comments ({comments.length})
                  </Label>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {comments.map((c) => (
                      <div key={c.id} className="flex gap-2">
                        <img src={c.user_avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${c.user_name}`} alt="" className="w-6 h-6 rounded-full shrink-0 mt-0.5" />
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="text-xs font-medium text-gray-900">{c.user_name}</span>
                            <span className="text-[10px] text-gray-400">{new Date(c.created_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                          </div>
                          <p className="text-sm text-gray-700 break-words" style={{ overflowWrap: 'anywhere' }}>{c.text}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <DialogFooter className="flex gap-2">
                <Button variant="outline" onClick={() => setIsViewDialogOpen(false)}>
                  Close
                </Button>
                <Button 
                  onClick={() => {
                    setIsViewDialogOpen(false);
                    openEditDialog(selectedTask);
                  }}
                  className="bg-[#5FD068] hover:bg-[#4ec057]"
                >
                  <Edit2 className="w-4 h-4 mr-2" />
                  Edit Task
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Task Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Task</DialogTitle>
          </DialogHeader>
          {selectedTask && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Task Title</Label>
                <Input
                  value={selectedTask.title}
                  onChange={(e) => setSelectedTask({ ...selectedTask, title: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={selectedTask.description}
                  onChange={(e) => setSelectedTask({ ...selectedTask, description: e.target.value })}
                  rows={4}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={selectedTask.status} onValueChange={(value) => setSelectedTask({ ...selectedTask, status: value })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Priority</Label>
                  <Select value={selectedTask.priority} onValueChange={(value) => setSelectedTask({ ...selectedTask, priority: value })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="low">Low</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select value={selectedTask.category || ''} onValueChange={(value) => setSelectedTask({ ...selectedTask, category: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="General">General</SelectItem>
                      <SelectItem value="Admin">Admin</SelectItem>
                      <SelectItem value="Data Entry">Data Entry</SelectItem>
                      <SelectItem value="Marketing">Marketing</SelectItem>
                      <SelectItem value="Research">Research</SelectItem>
                      <SelectItem value="Customer Support">Customer Support</SelectItem>
                      <SelectItem value="Content Writing">Content Writing</SelectItem>
                      <SelectItem value="Social Media">Social Media</SelectItem>
                      <SelectItem value="Bookkeeping">Bookkeeping</SelectItem>
                      <SelectItem value="Project Management">Project Management</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Due Date</Label>
                  <DatePicker
                    value={selectedTask.due_date || ''}
                    onChange={(date) => setSelectedTask({ ...selectedTask, due_date: date })}
                    placeholder="Select due date"
                  />
                </div>
              </div>

              {/* Comments Thread */}
              <div className="border-t pt-4 mt-4">
                <Label className="flex items-center gap-2 mb-3 text-gray-700">
                  <MessageSquare className="w-4 h-4" />
                  Comments ({comments.length})
                </Label>

                {/* Comment List */}
                <div className="space-y-3 max-h-48 overflow-y-auto mb-3">
                  {comments.length === 0 && (
                    <p className="text-sm text-gray-400 italic text-center py-2">No comments yet</p>
                  )}
                  {comments.map((c) => (
                    <div key={c.id} className="flex gap-2.5" data-testid={`comment-${c.id}`}>
                      <img
                        src={c.user_avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${c.user_name}`}
                        alt=""
                        className="w-7 h-7 rounded-full shrink-0 mt-0.5"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-medium text-gray-900">{c.user_name}</span>
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 capitalize">{c.user_role}</Badge>
                          <span className="text-[11px] text-gray-400">
                            {new Date(c.created_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                        <p className="text-sm text-gray-700 mt-0.5 break-words" style={{ overflowWrap: 'anywhere' }}>{c.text}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Add Comment */}
                <div className="flex gap-2">
                  <Input
                    placeholder="Add a comment..."
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAddComment(); }}}
                    className="flex-1"
                    data-testid="task-comment-input"
                  />
                  <Button
                    size="sm"
                    onClick={handleAddComment}
                    disabled={!newComment.trim() || commentLoading}
                    className="bg-[#5FD068] hover:bg-[#4ec057] px-3"
                    data-testid="task-comment-submit"
                  >
                    {commentLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleEditTask} className="bg-[#5FD068] hover:bg-[#4ec057]">
                  Save Changes
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Tasks;
